import { formatUserName } from "@impero-com/common/utils/formatting";
import { assertNever } from "@impero-com/common/utils/obj";
import * as Sentry from "@sentry/react";
import { FieldErrors, FormState } from "react-hook-form";

import {
    EffectivenessFieldTypeAnswer,
    UserOverview,
} from "api/generated/model";
import type {
    ActivityViewedStatus,
    ControlActivityOverview,
    ControlActivityOverviewListDto,
    ControlConclusionAnswerKind,
    ControlResultAssignmentKind,
    ControlResultStepGroupWithoutFields,
    ControlResultUserPoolOverview,
    FieldForControlConclusionFieldKindAndControlConclusionAnswerKind,
    FieldForSampleConclusionFieldKindAndSampleConclusionAnswerKind,
    FieldGroupAndExtendedFieldsForControlConclusionFieldKindAndControlConclusionAnswerKind,
    MapOfUpdateOrRemoveForUpdatableTestingActivitySample,
    SampleConclusionAnswerKind,
    TestingActivity,
    TestingActivityCurrentStep,
    TestingActivityKind,
    TestingActivitySample,
    TestingControlReviewerAnswerKind,
    UpdatableTestingActivity,
    UpdateOrRemoveForControlConclusionAnswerKind,
    UpdateOrRemoveForUpdatableTestingActivitySample,
} from "api/generated/model";
import { mapAsResourceWithoutAction } from "utils/dtoMapping";

import {
    ActivitySample,
    FieldsSchema,
    SAMPLES_KEY,
    TestingActivityForm,
} from "./schema";
import {
    ActivityFields,
    ActivityFieldsAnswers,
    ControlResultCurrentStep,
    ControlResultStepType,
    ControlSample,
    ResponsibleLabel,
} from "./types";

export function toStepFieldValues(
    activityKind: TestingActivityKind,
    currentStep: TestingActivityCurrentStep,
    controlConclusionFieldGroup: FieldGroupAndExtendedFieldsForControlConclusionFieldKindAndControlConclusionAnswerKind
) {
    switch (currentStep) {
        case "responsible":
            return toControlConclusionFieldValues(
                controlConclusionFieldGroup.fields,
                controlConclusionFieldGroup.answers
            );
        case "firstReviewer":
            switch (activityKind.kind) {
                case "WithFirstReviewer":
                case "WithSecondReviewer":
                    const { fields, answers } =
                        activityKind.firstReviewerFieldGroup;
                    return toControlConclusionFieldValues(fields, answers);
                case "ResponsibleOnly":
                    throw new Error("Unexpected activity kind");
                default:
                    return assertNever(activityKind);
            }
        case "secondReviewer":
            switch (activityKind.kind) {
                case "WithSecondReviewer":
                    const { fields, answers } =
                        activityKind.secondReviewerFieldGroup;
                    return toControlConclusionFieldValues(fields, answers);
                case "WithFirstReviewer":
                case "ResponsibleOnly":
                    throw new Error("Unexpected activity kind");
                default:
                    return assertNever(activityKind);
            }
        case "completed":
            return undefined;
        default:
            return assertNever(currentStep);
    }
}

export function toControlConclusionFieldValues(
    fields: ActivityFields,
    answers?: ActivityFieldsAnswers
): FieldsSchema {
    const defaultValues = fields.reduce((acc, field) => {
        const hasAnswer = answers && answers[field.id] !== undefined;
        return {
            [field.id]: hasAnswer ? answers[field.id].value : undefined,
            ...acc,
        };
    }, {});
    return defaultValues;
}

function toFormViewedStatus(status: ActivityViewedStatus | undefined) {
    const notViewed = {
        type: "NotViewed" as const,
    };
    if (status === undefined) {
        return notViewed;
    }
    switch (status.type) {
        case "NotViewed":
            return notViewed;
        case "Viewed":
            return {
                type: "Viewed" as const,
                value: {
                    at: new Date(status.value.at),
                },
            };
        default:
            assertNever(status);
    }
}

export function toActivitySampleValue(
    sampleConclusionFields: FieldForSampleConclusionFieldKindAndSampleConclusionAnswerKind[],
    activityId: string,
    answers?: ActivityFieldsAnswers,
    status?: ActivityViewedStatus
): ActivitySample {
    return {
        activityId,
        viewed: toFormViewedStatus(status),
        fields: toControlConclusionFieldValues(sampleConclusionFields, answers),
    };
}

export function toActivitySampleValues(
    controlActivitySamples: Record<string, TestingActivitySample>,
    sampleConclusionFields: FieldForSampleConclusionFieldKindAndSampleConclusionAnswerKind[]
): ActivitySample[] {
    return Object.entries(controlActivitySamples).map(([activityId, sample]) =>
        toActivitySampleValue(
            sampleConclusionFields,
            activityId,
            sample.answers,
            sample.status
        )
    );
}

function toResponsibleLabel(
    kind: ControlResultAssignmentKind,
    usersRes: UserOverview[],
    poolsRes: ControlResultUserPoolOverview[]
): ResponsibleLabel {
    switch (kind.type) {
        case "Pool":
            const poolRes = poolsRes.find(
                (pool) => pool.id === kind.userPoolId
            );
            if (poolRes === undefined) {
                throw new Error(
                    `Expected to find matching user pool in resource for id ${kind.userPoolId}`
                );
            }
            return {
                type: "Pool",
                userPoolId: kind.userPoolId,
                label: poolRes.name,
            };
        case "User":
            const userRes = usersRes.find((user) => user.id === kind.userId);
            if (userRes === undefined) {
                throw new Error(
                    `Expected to find matching user in resource for id ${kind.userId}`
                );
            }
            return {
                type: "User",
                userId: kind.userId,
                label: formatUserName(userRes),
            };
        default:
            assertNever(kind);
    }
}

function getTestingControlResultStepType(
    step: ControlResultStepGroupWithoutFields
): ControlResultStepType {
    if (step.subsequentSteps.length === 0) {
        return "responsibleOnly";
    }
    if (step.subsequentSteps.length === 1) {
        return "review";
    }
    if (step.subsequentSteps.length === 2) {
        return "secondReview";
    }
    throw new Error(
        `Unexpected number of subsequent steps: ${step.subsequentSteps.length}`
    );
}

export function getControlResultStatus(
    stepGroup: ControlResultStepGroupWithoutFields
): ControlResultCurrentStep {
    const allSteps = [stepGroup.initialStep, ...stepGroup.subsequentSteps];

    const incompleteIndex = allSteps.findIndex(
        (step) => !step.performance?.performedAt
    );
    const statuses: ControlResultCurrentStep[] = [
        "responsible",
        "firstReviewer",
        "secondReviewer",
    ];
    return incompleteIndex === -1 ? "completed" : statuses[incompleteIndex];
}

function getDueDate(resource: ControlActivityOverview): Date {
    const stepType = getTestingControlResultStepType(resource.stepGroup);
    switch (stepType) {
        case "responsibleOnly":
            return new Date(resource.stepGroup.initialStep.dueDate);
        case "review":
            return new Date(resource.stepGroup.subsequentSteps[0].dueDate);
        case "secondReview":
            return new Date(resource.stepGroup.subsequentSteps[1].dueDate);
        default:
            assertNever(stepType);
    }
}
export function toControlSamples(
    controlActivitiesList: ControlActivityOverviewListDto,
    samples: Record<string, TestingActivitySample>
): ControlSample[] {
    return controlActivitiesList.content.map((activity) => {
        const sample = samples?.[activity.resource.id];
        return {
            activityId: activity.resource.id,
            status: sample?.status ?? {
                type: "NotViewed",
            },
            tags: activity.resource.tags,
            coveredPeriod: activity.resource.coveredPeriod ?? null,
            title: activity.resource.title,
            dueDate: getDueDate(activity.resource),
            responsible: toResponsibleLabel(
                activity.resource.stepGroup.initialStep.kind,
                mapAsResourceWithoutAction(
                    controlActivitiesList.linkedResources.users
                ),
                controlActivitiesList.linkedResources.controlResultUserPools
            ),
            controlStatus: getControlResultStatus(activity.resource.stepGroup),
            hasViewRight: activity.actions.view,
            answers: sample?.answers ?? {},
            isRandom: sample?.isRandom ?? false,
        };
    });
}

// - If there are arrays, the function will not traverse objects within it
// If higher fidelity is needed these need to be mapped separately, as field arrays contain metadata not relevant to field values
// - Errors object can be provided to exclude errored, dirty field values
type DirtyFieldsType =
    | boolean
    | null
    | {
          [key: string]: DirtyFieldsType;
      }
    | DirtyFieldsType[];
export function getDirtyValues<T extends Record<string, any>>(
    dirtyFields: Partial<Record<keyof T, DirtyFieldsType>>,
    values: T,
    errors?: FieldErrors<T>
): Partial<T> {
    const dirtyValues = Object.keys(dirtyFields).reduce((prev, key) => {
        const value = dirtyFields[key];
        if (!value) {
            return prev;
        }
        const hasError = errors ? Boolean(errors?.[key]?.type) : false;
        if (hasError) {
            return prev;
        }
        const isObject = typeof value === "object";
        const isArray = Array.isArray(value);
        const nestedValue =
            isObject && !isArray
                ? getDirtyValues(
                      value as Record<string, any>,
                      values[key],
                      errors?.[key] as FieldErrors<T>
                  )
                : values[key];
        return { ...prev, [key]: isArray ? values[key] : nestedValue };
    }, {} as Partial<T>);
    return dirtyValues;
}

type FieldAnswerUpdate<T extends ControlConclusionAnswerKind> =
    | Extract<UpdateOrRemoveForControlConclusionAnswerKind, { type: "Remove" }>
    | (Omit<
          Extract<
              UpdateOrRemoveForControlConclusionAnswerKind,
              { type: "Update" }
          >,
          "value"
      > & { value: T });

export function toApiUpdateFields<
    T extends
        | ControlConclusionAnswerKind
        | SampleConclusionAnswerKind
        | TestingControlReviewerAnswerKind = ControlConclusionAnswerKind,
>(
    fieldValues: FieldsSchema | undefined,
    fieldGroupFields: FieldForControlConclusionFieldKindAndControlConclusionAnswerKind[]
): Record<string, FieldAnswerUpdate<T>> | undefined {
    if (fieldValues === undefined || Object.keys(fieldValues).length === 0) {
        return undefined;
    }

    const updateFields: Record<string, FieldAnswerUpdate<T>> = Object.entries(
        fieldValues
    ).reduce((acc, [fieldId, value]) => {
        const field = fieldGroupFields.find((field) => field.id === fieldId);

        if (!field) {
            throw new Error(
                `Field with id ${fieldId} not found in field group`
            );
        }

        const fieldType = field.fieldType.type;
        const isEmptyForFieldType = (val: any): boolean => {
            if (val === null || val === undefined) {
                return true;
            }

            switch (fieldType) {
                case "NumericInput":
                case "Effectiveness":
                    return false;

                case "RadioButtonGroup":
                case "Dropdown":
                case "TextInput":
                case "TextArea":
                    return typeof val === "string" && val.trim().length === 0;

                case "CheckboxGroup":
                    return (
                        !Array.isArray(val) ||
                        val.length === 0 ||
                        val.every(
                            (item) =>
                                typeof item === "string" &&
                                item.trim().length === 0
                        )
                    );

                case "FileUpload":
                    return !Array.isArray(val) || val.length === 0;

                case "Heading":
                    throw new Error("Not possible to change heading value");

                default:
                    return assertNever(fieldType);
            }
        };

        const emptyCondition = isEmptyForFieldType(value);

        const removeUpdate = {
            type: "Remove" as const,
        };
        const getUpdateValue =
            (): UpdateOrRemoveForControlConclusionAnswerKind => {
                switch (fieldType) {
                    case "Effectiveness":
                        return {
                            type: "Update",
                            value: {
                                type: "Effectiveness",
                                value: value as EffectivenessFieldTypeAnswer,
                            },
                        };
                    case "RadioButtonGroup":
                    case "Dropdown":
                        return emptyCondition
                            ? removeUpdate
                            : {
                                  type: "Update",
                                  value: {
                                      type: "Option",
                                      value: value as string,
                                  },
                              };
                    case "FileUpload":
                        return emptyCondition
                            ? removeUpdate
                            : {
                                  type: "Update",
                                  value: {
                                      type: "BlobIds",
                                      value: value as string[],
                                  },
                              };
                    case "CheckboxGroup":
                        return emptyCondition
                            ? removeUpdate
                            : {
                                  type: "Update",
                                  value: {
                                      type: "MultiOption",
                                      value: value as string[],
                                  },
                              };
                    case "TextArea":
                    case "TextInput":
                        return emptyCondition
                            ? removeUpdate
                            : {
                                  type: "Update",
                                  value: {
                                      type: "Text",
                                      value: value as string,
                                  },
                              };
                    case "NumericInput":
                        return emptyCondition
                            ? removeUpdate
                            : {
                                  type: "Update",
                                  value: {
                                      type: "Numeric",
                                      value: value as number,
                                  },
                              };
                    case "Heading":
                        throw new Error("Not possible to change heading value");
                    default:
                        assertNever(fieldType);
                }
            };

        return {
            ...acc,
            [fieldId]: getUpdateValue(),
        };
    }, {});
    return updateFields;
}

function toUpdateSamples(
    samples: ActivitySample[] | undefined,
    sampleConclusionFields: FieldForSampleConclusionFieldKindAndSampleConclusionAnswerKind[]
): MapOfUpdateOrRemoveForUpdatableTestingActivitySample | undefined {
    if (!samples || samples.length === 0) {
        return undefined;
    }

    const updateSamples: MapOfUpdateOrRemoveForUpdatableTestingActivitySample =
        samples.reduce((acc, sample) => {
            const updateSample: UpdateOrRemoveForUpdatableTestingActivitySample =
                {
                    type: "Update",
                    value: {
                        activityViewed: sample.viewed?.type === "Viewed",
                        answers: toApiUpdateFields<SampleConclusionAnswerKind>(
                            sample.fields,
                            sampleConclusionFields
                        ),
                    },
                };
            return {
                ...acc,
                [sample.activityId]: updateSample,
            };
        }, {});
    return updateSamples;
}

export function toUpdatableTestingActivity(
    formValues: Partial<TestingActivityForm>,
    resource: TestingActivity
): UpdatableTestingActivity {
    switch (resource.currentStep) {
        case "responsible":
            return {
                type: "responsible",
                controlConclusionFields: toApiUpdateFields(
                    formValues.fields,
                    resource.controlConclusionFieldGroup.fields
                ),
                samples: toUpdateSamples(
                    formValues[SAMPLES_KEY],
                    resource.sampleConclusionFieldGroup.fields
                ),
            };
        case "firstReviewer":
            if (resource.activityKind.kind === "ResponsibleOnly") {
                throw new Error("Unexpected activity kind");
            }
            return {
                type: "reviewer",
                reviewerFields:
                    toApiUpdateFields<TestingControlReviewerAnswerKind>(
                        formValues.fields,
                        resource.activityKind.firstReviewerFieldGroup.fields
                    ),
            };
        case "secondReviewer":
            if (resource.activityKind.kind !== "WithSecondReviewer") {
                throw new Error("Unexpected activity kind");
            }
            return {
                type: "secondReviewer",
                reviewerFields:
                    toApiUpdateFields<TestingControlReviewerAnswerKind>(
                        formValues.fields,
                        resource.activityKind.secondReviewerFieldGroup.fields
                    ),
            };
        case "completed":
            throw new Error("Cannot update completed activity");
        default:
            assertNever(resource.currentStep);
    }
}

export function toDefaultActivitySampleValue(
    sampleConclusionFields: FieldForSampleConclusionFieldKindAndSampleConclusionAnswerKind[],
    activityId: string
): ActivitySample {
    return {
        activityId,
        viewed: {
            type: "NotViewed",
        },
        fields: toControlConclusionFieldValues(sampleConclusionFields),
    };
}

export function formStateToUpdatableTestingActivity(
    getValues: () => TestingActivityForm,
    formState: FormState<TestingActivityForm>,
    resource: TestingActivity
): UpdatableTestingActivity | void {
    const formValues = getValues();
    // Check if there are any dirty fields at the root level
    const hasRootDirtyFields =
        formState.dirtyFields.fields || formState.dirtyFields.submission;

    // Check if there are any dirty samples
    const hasDirtySamples =
        formState.dirtyFields.samples &&
        formValues.samples?.some((_, index) => {
            const sampleDirtyFields = formState.dirtyFields.samples?.[index];
            return sampleDirtyFields?.fields || sampleDirtyFields?.viewed;
        });

    // If nothing is dirty, don't submit
    if (!hasRootDirtyFields && !hasDirtySamples) {
        return;
    }

    // Build dirty values object
    const dirtyValues: Partial<TestingActivityForm> = {};

    // Add root level dirty fields (excluding samples)
    if (hasRootDirtyFields) {
        const rootDirtyValues = getDirtyValues(
            formState.dirtyFields,
            formValues
        );
        // Remove samples from root dirty values since we handle them separately
        const { [SAMPLES_KEY]: _, ...rootFields } = rootDirtyValues;
        Object.assign(dirtyValues, rootFields);
    }

    // Handle samples separately
    if (hasDirtySamples && formValues.samples) {
        const dirtySamples: ActivitySample[] = [];

        formValues.samples.forEach((sample, index) => {
            // sc-30721 In Safari it is possible to get a sample without activityId after a removal.
            // Let's skip them and log a message in sentry, to see if this hack can be removed.
            // Note: Here the invalid value comes from getValues(), which wrongly contains partial deleted entries.
            if (!sample.activityId) {
                Sentry.captureMessage(
                    "Sample without activityId, this is a known bug in Safari",
                    (scope) => {
                        return scope.setContext("samples", {
                            sample,
                        });
                    }
                );
                return;
            }

            const sampleDirtyFields = formState.dirtyFields.samples?.[index];
            const hasDirtyFields =
                sampleDirtyFields?.fields &&
                Object.keys(sampleDirtyFields.fields).length > 0;
            const hasDirtyViewed = sampleDirtyFields?.viewed;

            // Only include samples that actually have changes
            if (hasDirtyFields || hasDirtyViewed) {
                const sampleValue = formValues[SAMPLES_KEY][index];
                const dirtyFields = hasDirtyFields
                    ? getDirtyValues(sampleDirtyFields.fields!, sample.fields)
                    : {};

                dirtySamples.push({
                    ...sampleValue,
                    fields: dirtyFields,
                });
            }
        });

        if (dirtySamples.length > 0) {
            dirtyValues[SAMPLES_KEY] = dirtySamples;
        }
    }

    // Convert to API format and submit
    const reformat = toUpdatableTestingActivity(dirtyValues, resource);
    return reformat;
}
